import React from 'react';
import { DistinctIdLink } from 'features/agent-analytics/components/runCells';
import { RunIdLink, ParentSpanLink } from 'features/agent-analytics/components/spanCells';
// Promoted to shared so global-search destinations (users, events, issues)
// can mark matches too. Re-exported here so payload consumers didn't move.
import { HighlightContext, HighlightText } from 'shared/components/HighlightText';

export { HighlightContext, HighlightText };

/** RFC-4122 UUID (v1–v5). */
export const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;

const RUN_KEYS = new Set([
  'run_id', 'runid', 'parent_run_id', 'parent_runid',
]);
const SPAN_KEYS = new Set([
  'span_id', 'spanid', 'parent_span_id', 'parent_spanid',
]);
const USER_KEYS = new Set([
  'distinct_id', 'distinctid', 'user_id', 'userid',
]);

function normKey(k) {
  return String(k || '').toLowerCase().replace(/-/g, '_');
}

export function entityKindForKey(key) {
  const k = normKey(key);
  if (RUN_KEYS.has(k)) return 'run';
  if (SPAN_KEYS.has(k)) return 'span';
  if (USER_KEYS.has(k)) return 'user';
  return null;
}

function isEmailLike(s) {
  return typeof s === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

/**
 * Render a primitive JSON value; link run/span/user ids when handlers are provided.
 */
export function PayloadEntityValue({ value, jsonKey, onRunClick, onSpanClick }) {
  if (value === null || value === undefined) {
    return <span className="text-[color:var(--text-muted)]">—</span>;
  }
  if (typeof value === 'boolean' || typeof value === 'number') {
    return <span className="font-mono text-[12px] text-[color:var(--text-primary)]">{String(value)}</span>;
  }
  const text = String(value);
  if (text === '') return <span className="text-[color:var(--text-muted)]">—</span>;

  const kind = entityKindForKey(jsonKey);
  if (kind === 'run' && onRunClick) {
    return <RunIdLink value={text} onRunClick={onRunClick} compact={false} />;
  }
  if (kind === 'span' && onSpanClick) {
    return <ParentSpanLink value={text} onSpanClick={onSpanClick} />;
  }
  if (kind === 'user' || (jsonKey == null && isEmailLike(text))) {
    return <DistinctIdLink value={text} />;
  }
  if (!jsonKey && onRunClick) {
    const trimmed = text.trim();
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(trimmed)) {
      return <RunIdLink value={trimmed} onRunClick={onRunClick} compact={false} />;
    }
  }
  if (onRunClick || onSpanClick) {
    return (
      <span className="whitespace-pre-wrap break-words text-[12px] text-[color:var(--text-primary)]">
        <LinkifiedPlainText text={text} onRunClick={onRunClick} onSpanClick={onSpanClick} />
      </span>
    );
  }
  return (
    <span className="whitespace-pre-wrap break-words text-[12px] text-[color:var(--text-primary)]">
      <HighlightText text={text} />
    </span>
  );
}

/** Split plain text and wrap UUID tokens as run links (mixed handoff lists, prose). */
export function LinkifiedPlainText({ text, onRunClick, onSpanClick }) {
  if (!text) return null;
  if (!onRunClick && !onSpanClick) return text;

  const re = new RegExp(UUID_RE.source, 'gi');
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: 'text', value: text.slice(last, m.index) });
    parts.push({ type: 'uuid', value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });
  if (!parts.length) return text;

  return parts.map((p, i) => {
    if (p.type === 'text') return <React.Fragment key={i}><HighlightText text={p.value} /></React.Fragment>;
    if (onRunClick) {
      return (
        <RunIdLink
          key={i}
          value={p.value}
          onRunClick={onRunClick}
          compact={false}
        />
      );
    }
    if (onSpanClick) {
      return (
        <ParentSpanLink
          key={i}
          value={p.value}
          onSpanClick={onSpanClick}
        />
      );
    }
    return p.value;
  });
}

/** JSON <pre> view with clickable UUIDs (JSON tab). */
export function LinkifiedPre({ text, onRunClick, onSpanClick }) {
  if (!text) return null;
  if (!onRunClick && !onSpanClick) {
    return (
      <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap rounded bg-[color:var(--surface-sunken)] p-2 text-[11px] text-[color:var(--text-primary)]">
        {text}
      </pre>
    );
  }
  return (
    <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap rounded bg-[color:var(--surface-sunken)] p-2 text-[11px] text-[color:var(--text-primary)]">
      <LinkifiedPlainText text={text} onRunClick={onRunClick} onSpanClick={onSpanClick} />
    </pre>
  );
}
